import React, { useState, useEffect } from 'react';
import { Modal, Steps, Button, message } from 'antd';
import { CaseParty, CaseDetail } from '../../../../services/case';
import BasicInfoStep from './BasicInfoStep';
import OurPartyStep from './OurPartyStep';
import OtherPartyStep from './OtherPartyStep';
import DetailStep from './DetailStep';
import FeeStep from './FeeStep';
import ConfirmStep from './ConfirmStep';
import { createCase, updateCase, getCaseById, PartyType } from '../../../../services/case';
import { handleApiError, showSuccess, isValidationError, formatFormErrors } from '../../../../utils/errorHandler';
import dayjs from 'dayjs';

const { Step } = Steps;

// Wizard state interface
export interface BasicInfoData {
  case_name: string;
  case_type?: string;
  case_stage?: string;
  case_status?: string;
  accept_date: string;
  court_date?: string;
  verdict_date?: string;
  responsible_lawyer_id: number;
  assistant_lawyer_id?: number;
  lead_id?: number;
  wechat_group_id?: number;
}

export interface FeeData {
  fee_method?: string;
  contract_amount?: number;
  received_amount?: number;
  pending_amount?: number;
  fee_description?: string;
  secondary_reason?: string;
}

export interface WizardFormData {
  basicInfo: Partial<BasicInfoData>;
  ourParties: CaseParty[];
  otherParties: CaseParty[];
  detail: Partial<CaseDetail>;
  fee: Partial<FeeData>;
}

interface CaseWizardProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (caseId: number) => void;
  mode?: 'create' | 'edit';
  caseId?: number;
}

const STORAGE_KEY = 'case_wizard_data';

const CaseWizard: React.FC<CaseWizardProps> = ({ visible, onClose, onSuccess, mode = 'create', caseId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<WizardFormData>({
    basicInfo: {},
    ourParties: [],
    otherParties: [],
    detail: {},
    fee: {},
  });

  // Load existing case data in edit mode
  useEffect(() => {
    const loadCaseData = async () => {
      if (visible && mode === 'edit' && caseId) {
        setLoading(true);
        try {
          const caseData = await getCaseById(caseId);
          
          // Transform case data to wizard format
          setFormData({
            basicInfo: {
              case_name: caseData.case_name,
              accept_date: caseData.accept_date,
              responsible_lawyer_id: caseData.responsible_lawyer_id,
              assistant_lawyer_id: caseData.assistant_lawyer_id,
              lead_id: caseData.lead_id,
              wechat_group_id: (caseData as any).wechat_group_id,
              case_status: (caseData as any).case_status || 'active',
            },
            ourParties: (caseData.parties || []).filter(p => p.party_type === PartyType.OUR_SIDE),
            otherParties: (caseData.parties || []).filter(p => p.party_type === PartyType.OTHER_SIDE),
            detail: {
              ...(caseData.details || {}),
              // 将这些字段也放入 detail 中，供 DetailStep 使用
              case_type: caseData.case_type,
              case_number: (caseData as any).case_number,
              court_date: caseData.court_date,
              verdict_date: caseData.verdict_date,
            } as any,
            fee: {},
          });
        } catch (error) {
          console.error('Failed to load case data:', error);
          message.error('加载案件数据失败');
          onClose();
        } finally {
          setLoading(false);
        }
      }
    };

    loadCaseData();
  }, [visible, mode, caseId]);

  // Load data from localStorage on mount (only in create mode)
  useEffect(() => {
    if (visible && mode === 'create') {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setFormData(parsed);
        } catch (error) {
          console.error('Failed to parse saved wizard data:', error);
        }
      }
    }
  }, [visible, mode]);

  // Save data to localStorage whenever it changes (only in create mode)
  useEffect(() => {
    if (visible && mode === 'create') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  }, [formData, visible, mode]);

  const steps = [
    { title: '基本信息', key: 'basic' },
    { title: '我方信息', key: 'ourParty' },
    { title: '对方信息', key: 'otherParty' },
    { title: '案件详情', key: 'detail' },
    { title: '确认提交', key: 'confirm' },
  ];

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Basic Info
        if (!formData.basicInfo.case_name?.trim()) {
          message.error('请输入案件名称');
          return false;
        }
        if (!formData.basicInfo.accept_date) {
          message.error('请选择承接日期');
          return false;
        }
        if (!formData.basicInfo.responsible_lawyer_id) {
          message.error('请选择负责律师');
          return false;
        }
        return true;

      case 1: // Our Party
        if (formData.ourParties.length === 0) {
          message.error('请至少添加一个我方当事人');
          return false;
        }
        return true;

      case 2: // Other Party
        // Other party is optional
        return true;

      case 3: // Detail
        // Detail is optional
        return true;

      case 4: // Confirm
        // Confirm step
        return true;

      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Helper function to format date
      const formatDate = (date: any, format: string = 'YYYY-MM-DD') => {
        if (!date) return undefined;
        if (dayjs.isDayjs(date)) return date.format(format);
        if (typeof date === 'string') return date;
        return dayjs(date).format(format);
      };

      // Prepare the data for submission
      const submitData: any = {
        // Basic info (required fields)
        case_name: formData.basicInfo.case_name!,
        accept_date: formatDate(formData.basicInfo.accept_date),
        responsible_lawyer_id: formData.basicInfo.responsible_lawyer_id!,
        case_status: formData.basicInfo.case_status || 'active',

        // Parties
        parties: [...formData.ourParties, ...formData.otherParties],

        // Detail
        detail: {},
      };

      // Add optional basic info fields from detail step
      if ((formData.detail as any).case_type) {
        submitData.case_type = (formData.detail as any).case_type;
      }
      if ((formData.detail as any).case_number) {
        submitData.case_number = (formData.detail as any).case_number;
      }
      if ((formData.detail as any).case_stage) {
        submitData.case_stage = (formData.detail as any).case_stage;
      }
      if ((formData.detail as any).court_date) {
        submitData.court_date = formatDate((formData.detail as any).court_date, 'YYYY-MM-DD HH:mm:ss');
      }
      if ((formData.detail as any).verdict_date) {
        submitData.verdict_date = formatDate((formData.detail as any).verdict_date);
      }

      // Add optional basic info fields
      if (formData.basicInfo.assistant_lawyer_id) {
        submitData.assistant_lawyer_id = formData.basicInfo.assistant_lawyer_id;
      }
      if (formData.basicInfo.lead_id) {
        submitData.lead_id = formData.basicInfo.lead_id;
      }
      if (formData.basicInfo.wechat_group_id) {
        submitData.wechat_group_id = formData.basicInfo.wechat_group_id;
      }

      // Add optional detail fields
      if (formData.detail.case_reason) {
        submitData.detail.case_reason = formData.detail.case_reason;
      }
      if (formData.detail.case_level) {
        submitData.detail.case_level = formData.detail.case_level;
      }
      if (formData.detail.court_name) {
        submitData.detail.court_name = formData.detail.court_name;
      }
      if (formData.detail.court_contact) {
        submitData.detail.court_contact = formData.detail.court_contact;
      }
      if (formData.detail.case_summary) {
        submitData.detail.case_summary = formData.detail.case_summary;
      }
      if (formData.detail.notes) {
        submitData.detail.notes = formData.detail.notes;
      }

      let response;
      if (mode === 'edit' && caseId) {
        // Update existing case
        response = await updateCase(caseId, submitData);
        showSuccess('案件更新成功');
      } else {
        // Create new case
        response = await createCase(submitData);
        showSuccess('案件创建成功');
        
        // Clear localStorage only in create mode
        localStorage.removeItem(STORAGE_KEY);
      }

      // Reset form
      setFormData({
        basicInfo: {},
        ourParties: [],
        otherParties: [],
        detail: {},
        fee: {},
      });
      setCurrentStep(0);

      // Call success callback
      onSuccess(response.id);
      onClose();
    } catch (error: any) {
      console.error(`Failed to ${mode} case:`, error);

      // Use unified error handler
      handleApiError(error, {
        400: '案件数据验证失败，请检查输入',
        409: '案件编号已存在，请修改后重试',
      });

      // If validation error, show which step has errors
      if (isValidationError(error)) {
        const formErrors = formatFormErrors(error);
        if (formErrors.length > 0) {
          message.warning('请检查表单中的错误信息');
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (mode === 'edit') {
      // In edit mode, just close without confirmation
      onClose();
    } else {
      // In create mode, show confirmation
      Modal.confirm({
        title: '确认关闭',
        content: '关闭后数据将保存在本地，下次打开时可以继续填写',
        okText: '确认',
        cancelText: '取消',
        onOk: () => {
          onClose();
        },
      });
    }
  };

  const updateFormData = (section: keyof WizardFormData, data: any) => {
    setFormData((prev) => ({
      ...prev,
      [section]: data,
    }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <BasicInfoStep
            data={formData.basicInfo}
            onChange={(data) => updateFormData('basicInfo', data)}
          />
        );
      case 1:
        return (
          <OurPartyStep
            data={formData.ourParties}
            onChange={(data) => updateFormData('ourParties', data)}
          />
        );
      case 2:
        return (
          <OtherPartyStep
            data={formData.otherParties}
            onChange={(data) => updateFormData('otherParties', data)}
          />
        );
      case 3:
        return (
          <DetailStep
            data={formData.detail}
            onChange={(data) => updateFormData('detail', data)}
          />
        );
      case 4:
        return <ConfirmStep formData={formData} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      title={mode === 'edit' ? '编辑案件' : '新建案件'}
      open={visible}
      onCancel={handleCancel}
      width={900}
      footer={null}
      destroyOnClose={false}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <span>加载中...</span>
        </div>
      ) : (
        <>
          <Steps current={currentStep} style={{ marginBottom: 24 }}>
            {steps.map((step) => (
              <Step key={step.key} title={step.title} />
            ))}
          </Steps>

          <div style={{ minHeight: 400, marginBottom: 24 }}>
            {renderStepContent()}
          </div>
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleCancel}>取消</Button>
        <div>
          {currentStep > 0 && (
            <Button style={{ marginRight: 8 }} onClick={handlePrev}>
              上一步
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={handleNext}>
              下一步
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <Button type="primary" loading={submitting} onClick={handleSubmit}>
              {mode === 'edit' ? '更新' : '提交'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CaseWizard;
